import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Calendar, Clock } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import rehypeRaw from 'rehype-raw';
import rehypeSanitize from 'rehype-sanitize';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { tomorrow } from 'react-syntax-highlighter/dist/esm/styles/prism';
import * as emoji from 'node-emoji';

const BASE_URL = 'http://34.47.145.31:8000/api'

const BlogPost = () => {
  const [post, setPost] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const { slug } = useParams();
  const navigate = useNavigate();

  useEffect(() => {
    const fetchPost = async () => {
      try {
        setIsLoading(true);
        console.log('Fetching post with slug:', slug);
        const response = await fetch(`http://34.47.145.31:8000/api/blogs/${slug}`, {
          credentials: 'include',
          headers: {
            'Accept': 'application/json'
          }
        });
        
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        
        const data = await response.json();
        console.log('Received post data:', data);
        setPost(data);
      } catch (err) {
        console.error('Error fetching post:', err);
        setError(err.message || 'Failed to load blog post');
      } finally {
        setIsLoading(false);
      }
    };

    if (slug) {
      fetchPost();
    }
  }, [slug]);

  const processContent = (content) => {
    if (!content) return '';

    try {
      // Handle array or object content
      if (typeof content === 'object') {
        if (Array.isArray(content)) {
          return content.map(block => block.raw || block.text || '').join('\n\n');
        }
        return content.raw || content.text || JSON.stringify(content);
      }

      // Process the content
      let processedContent = String(content)
        .replace(/\\n/g, '\n')
        .replace(/\\"/g, '"')
        .replace(/^"|"$/g, '')
        // Fix image markdown syntax
        .replace(/!\[\]\((https?:\/\/[^\s)]+)\)/g, (match, url) => `![](${url})`)
        // Fix link markdown syntax
        .replace(/\[([^\]]+)\]\((https?:\/\/[^\s)]+)\)/g, (match, text, url) => `[${text}](${url})`)
        // Clean up any remaining escapes
        .replace(/\\/g, '')
        .replace(/\[object Object\]/g, '')
        .replace(/undefined/g, '')
        .trim();

      return processedContent;
    } catch (err) {
      console.error('Error processing content:', err);
      return String(content);
    }
  };

  return (
    <div className="blog-post-wrapper">
      <style>
        {`
          .blog-post-wrapper {
            min-height: 100vh;
            padding: 2rem 1rem;
            background-color: #f8fafc;
            color: #1a202c;
          }

          .dark .blog-post-wrapper {
            background-color: #0f172a;
            color: #e2e8f0;
          }

          .blog-container {
            max-width: 800px;
            margin: 0 auto;
          }

          .prose {
            font-family: system-ui, -apple-system, sans-serif;
            line-height: 1.75;
            font-size: 1.125rem;
            color: #334155;
          }

          .dark .prose {
            color: #e2e8f0;
          }

          .prose h1,
          .prose h2,
          .prose h3,
          .prose h4,
          .prose h5,
          .prose h6 {
            font-weight: 700;
            line-height: 1.3;
            margin: 2rem 0 1rem;
            color: #1e293b;
          }

          .dark .prose h1,
          .dark .prose h2,
          .dark .prose h3,
          .dark .prose h4,
          .dark .prose h5,
          .dark .prose h6 {
            color: #f1f5f9;
          }

          .prose h1 { font-size: 2.25rem; }
          .prose h2 { font-size: 1.875rem; }
          .prose h3 { font-size: 1.5rem; }
          .prose h4 { font-size: 1.25rem; }
          .prose h5 { font-size: 1.125rem; }
          .prose h6 { font-size: 1rem; }

          .prose p {
            margin: 1.5rem 0;
            line-height: 1.8;
          }

          .prose a {
            color: #2563eb;
            text-decoration: none;
            border-bottom: 1px solid transparent;
            transition: all 0.2s ease;
            font-weight: 500;
          }

          .prose a:hover {
            color: #1d4ed8;
            border-bottom-color: currentColor;
          }

          .dark .prose a {
            color: #60a5fa;
          }

          .dark .prose a:hover {
            color: #93c5fd;
          }

          .prose strong {
            font-weight: 600;
            color: #1e293b;
          }

          .dark .prose strong {
            color: #f1f5f9;
          }

          .prose em {
            font-style: italic;
          }

          .prose ul,
          .prose ol {
            margin: 1.5rem 0;
            padding-left: 1.5rem;
          }

          .prose ul { list-style-type: disc; }
          .prose ol { list-style-type: decimal; }

          .prose li {
            margin: 0.5rem 0;
            line-height: 1.7;
          }

          .prose blockquote {
            border-left: 4px solid #3b82f6;
            margin: 1.5rem 0;
            padding: 1rem 1.5rem;
            background: #f8fafc;
            border-radius: 0.5rem;
            color: #1e293b;
            font-style: italic;
          }

          .dark .prose blockquote {
            background: #1e293b;
            color: #f1f5f9;
          }

          .prose img {
            max-width: 100%;
            height: auto;
            border-radius: 0.5rem;
            margin: 2rem auto;
            display: block;
            box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
            transition: transform 0.2s ease;
          }

          .prose img:hover {
            transform: scale(1.01);
          }

          .prose pre {
            background: #1e293b;
            border-radius: 0.5rem;
            padding: 1rem;
            margin: 1.5rem 0;
            overflow-x: auto;
          }

          .prose code {
            font-family: 'Fira Code', monospace;
            font-size: 0.875rem;
          }

          .prose :not(pre) > code {
            background: #f1f5f9;
            color: #dc2626;
            padding: 0.2rem 0.4rem;
            border-radius: 0.25rem;
            font-size: 0.875rem;
          }

          .dark .prose :not(pre) > code {
            background: #1e293b;
            color: #f87171;
          }

          .blog-title {
            font-size: 2.5rem;
            font-weight: 800;
            color: #1e293b;
            margin-bottom: 1rem;
            line-height: 1.2;
          }

          .dark .blog-title {
            color: #f1f5f9;
          }

          .blog-metadata {
            display: flex;
            gap: 1.5rem;
            margin-bottom: 2rem;
            color: #64748b;
          }

          .metadata-item {
            display: flex;
            align-items: center;
            gap: 0.5rem;
            font-size: 0.875rem;
          }

          .link-styles a {
            color: #2563eb !important;
            text-decoration: none !important;
            border-bottom: 1px solid transparent !important;
            transition: all 0.2s ease !important;
            font-weight: 500 !important;
          }

          .link-styles a:hover {
            color: #1d4ed8 !important;
            border-bottom-color: currentColor !important;
          }

          .dark .link-styles a {
            color: #60a5fa !important;
          }

          .dark .link-styles a:hover {
            color: #93c5fd !important;
          }

          @media (max-width: 640px) {
            .blog-title {
              font-size: 2rem;
            }

            .blog-metadata {
              flex-direction: column;
              gap: 0.75rem;
            }

            .prose {
              font-size: 1rem;
            }

            .prose h1 { font-size: 1.875rem; }
            .prose h2 { font-size: 1.5rem; }
            .prose h3 { font-size: 1.25rem; }
          }
        `}
      </style>

      <div className="blog-container">
        <button
          onClick={() => navigate('/blog')}
          className="button-custom"
        >
          <ArrowLeft size={20} className="group-hover:-translate-x-1 transition-transform" />
          <span>Back to Blogs</span>
        </button>

        {isLoading ? (
          <div className="space-y-4">
            <div className="loading-skeleton h-8 w-3/4" />
            <div className="loading-skeleton h-4 w-1/2" />
            <div className="loading-skeleton h-96" />
          </div>
        ) : error ? (
          <div className="bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 p-6 rounded-lg">
            <h2 className="text-xl font-bold mb-2">Error Loading Post</h2>
            <p>{error}</p>
          </div>
        ) : post ? (
          <article className="bg-white dark:bg-gray-800 rounded-xl shadow-lg overflow-hidden">
            {post.coverImage && (
              <div className="relative h-[400px] overflow-hidden">
                <img
                  src={post.coverImage}
                  alt={post.title}
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    e.target.src = '/api/placeholder/800/400';
                    e.target.onerror = null;
                  }}
                />
              </div>
            )}
            
            <div className="p-8">
              <h1 className="blog-title">{post.title}</h1>
              
              <div className="blog-metadata">
                <div className="metadata-item">
                  <Calendar size={16} />
                  <span>{new Date(post.dateAdded).toLocaleDateString()}</span>
                </div>
                <div className="metadata-item">
                  <Clock size={16} />
                  <span>{post.readTime} min read</span>
                </div>
              </div>

              {post.brief && (
                <div className="text-lg text-gray-600 dark:text-gray-300 mb-8 border-l-4 border-blue-500 pl-4 py-2">
                  {post.brief}
                </div>
              )}

              <div className="prose dark:prose-invert max-w-none link-styles">
                <ReactMarkdown
                  rehypePlugins={[rehypeRaw, rehypeSanitize]}
                  components={{
                    code({node, inline, className, children, ...props}) {
                      const match = /language-(\w+)/.exec(className || '');
                      const content = emoji.emojify(String(children).replace(/\n$/, ''));
                      
                      return !inline && match ? (
                        <SyntaxHighlighter
                          style={tomorrow}
                          language={match[1]}
                          PreTag="div"
                          {...props}
                        >
                          {content}
                        </SyntaxHighlighter>
                      ) : (
                        <code className={className} {...props}>
                          {content}
                        </code>
                      );
                    },
                    a({node, children, href, ...props}) {
                      return (
                        <a
                          href={href}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300"
                          {...props}
                        >
                          {children}
                        </a>
                      );
                    },
                    img({src, alt, ...props}) {
                      return (
                        <img
                          src={src}
                          alt={alt || ''}
                          loading="lazy"
                          className="rounded-lg shadow-lg max-w-full mx-auto"
                          onError={(e) => {
                            e.target.src = '/api/placeholder/800/400';
                            e.target.onerror = null;
                          }}
                          {...props}
                        />
                      );
                    }
                  }}
                >
                  {processContent(post.content)}
                </ReactMarkdown>
              </div>
            </div>
          </article>
        ) : null}
      </div>
    </div>
  );
};

export default BlogPost;